import React, { useState } from "react";

import ClozeSentence from "./ClozeSentence";
import Markdown from "./Markdown";

const CEFR_LEVELS = ["a1", "a2", "b1", "b2", "c1", "c2"];

const CLOZE_REGEX = /\{\{(.+?)\}\}/;

// One field, then review. Deliberately not a form asking the learner to author a topic
// name, scenario and example sentences — someone who wants to drill "por vs para" is the
// least equipped to write the model sentence, since that's the thing they don't know.
export default function GrammarUserExercise({ backUrl, previewUrl, createUrl, quota, targetLanguageName }) {
  const [request, setRequest] = useState("");
  const [cefrLevel, setCefrLevel] = useState("");
  const [draft, setDraft] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  // AI checks used this month, shared across features - so taken from each response,
  // not counted here.
  const [used, setUsed] = useState((quota || {}).countThisMonth || 0);

  const outOfQuota = !!quota && used >= quota.maxPerMonth;

  const preview = (e) => {
    if (e) e.preventDefault();
    if (!request.trim()) return;
    setWorking(true);
    setError(null);
    $.ajax({
      method: "post",
      url: previewUrl,
      data: { request: request, cefr_level: cefrLevel }
    })
      .done((data) => {
        setDraft(data.draft);
        if (data.aiChecks) setUsed(data.aiChecks.countThisMonth);
      })
      .fail((xhr) => {
        const json = xhr.responseJSON || {};
        if (json.aiChecks) setUsed(json.aiChecks.countThisMonth);
        setError(json.errors ? json.errors[0] : "Couldn't build that exercise. Please try again.");
      })
      .always(() => setWorking(false));
  };

  const updateSentence = (i, update) => {
    setDraft((prev) => ({
      ...prev,
      sentences: prev.sentences.map((s, j) => (j === i ? { ...s, ...update } : s))
    }));
  };

  const removeSentence = (i) => {
    setDraft((prev) => ({ ...prev, sentences: prev.sentences.filter((_, j) => j !== i) }));
  };

  const save = () => {
    setWorking(true);
    setError(null);
    $.ajax({ method: "post", url: createUrl, data: { draft: draft } })
      .done((data) => { window.location = data.url; })
      .fail((xhr) => {
        const errors = (xhr.responseJSON || {}).errors;
        setError(errors ? errors[0] : "Couldn't save that exercise. Please try again.");
        setWorking(false);
      });
  };

  const renderQuota = () => {
    if (!quota) return null;
    return (
      <p className="text-muted quota">
        {outOfQuota
          ? `You've used all ${quota.maxPerMonth.toLocaleString()} of this month's AI checks — they reset in ${quota.monthResetsInDays} days.`
          : `${used.toLocaleString()} / ${quota.maxPerMonth.toLocaleString()} AI checks used this month.`}
        {" "}This is in beta; limits may change.
      </p>
    );
  };

  if (!draft) {
    return (
      <div className="grammar-user-exercise">
        <div className="text-center">
          <h1 className="joystix">Create your own exercise</h1>
          <p className="lead">
            Tell us what you want to practise and we'll build a set of {targetLanguageName} sentences for it.
          </p>
        </div>

        <form onSubmit={preview}>
          <div className="form-group">
            <label htmlFor="grammar-request">What do you want to practise?</label>
            <textarea
              id="grammar-request"
              className="form-control"
              rows={3}
              autoFocus
              disabled={working}
              value={request}
              maxLength={300}
              placeholder="e.g. past tense endings, or forming questions"
              onChange={(e) => setRequest(e.target.value)}
            />
            <span className="help-block">
              Plain words are fine — you don't need to know what the grammar point is called.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="grammar-cefr">Your level (optional)</label>
            <select
              id="grammar-cefr"
              className="form-control"
              disabled={working}
              value={cefrLevel}
              onChange={(e) => setCefrLevel(e.target.value)}
            >
              <option value="">Let us choose</option>
              {CEFR_LEVELS.map((level) => (
                <option key={level} value={level}>{level.toUpperCase()}</option>
              ))}
            </select>
          </div>

          <p>
            <button
              type="submit"
              className="btn btn-success btn-lg joystix"
              disabled={working || outOfQuota || !request.trim()}
            >
              {working ? (
                <span><span className="glyphicon glyphicon-refresh spin" /> Building...</span>
              ) : "Build my exercise"}
            </button>
          </p>
          {error && <p className="text-danger">{error}</p>}
          {renderQuota()}
          <p><a href={backUrl}>← Back to topics</a></p>
        </form>
      </div>
    );
  }

  return (
    <div className="grammar-user-exercise">
      <div className="text-center">
        <h1 className="joystix">{draft.exercise_name}</h1>
        <p className="text-muted">
          {draft.topic_name}
          {draft.cefr_level && <span> · {draft.cefr_level.toUpperCase()}</span>}
          {draft.exercise_description && <span> · {draft.exercise_description}</span>}
        </p>
        <p className="lead">Have a look before saving — edit or remove anything that isn't right.</p>
      </div>

      {draft.lesson && (
        <div className="panel panel-default">
          <div className="panel-heading">How it works</div>
          <div className="panel-body">
            <Markdown>{draft.lesson}</Markdown>
          </div>
        </div>
      )}

      {draft.model_sentence && (
        <div className="well well-sm">
          <strong className="text-muted">Example</strong>
          <p><ClozeSentence text={draft.model_sentence} /></p>
        </div>
      )}

      <h3>{draft.sentences.length} sentences</h3>
      {draft.sentences.map((sentence, i) => {
        const valid = CLOZE_REGEX.test(sentence.text);
        return (
          <div key={i} className="draft-sentence">
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                value={sentence.text}
                onChange={(e) => updateSentence(i, { text: e.target.value })}
              />
              {!valid && (
                <span className="help-block text-danger">
                  Needs exactly one answer wrapped in {"{{"}braces{"}}"} — this one won't be saved.
                </span>
              )}
            </div>
            <div className="form-group">
              <input
                type="text"
                className="form-control input-sm"
                value={sentence.translation || ""}
                placeholder="Translation"
                onChange={(e) => updateSentence(i, { translation: e.target.value })}
              />
            </div>
            <p className="text-muted">
              {sentence.hint && <span>Hint: {sentence.hint} · </span>}
              <button type="button" className="btn btn-link btn-xs" onClick={() => removeSentence(i)}>
                Remove
              </button>
            </p>
          </div>
        );
      })}

      <div className="text-center" style={{ marginTop: 30 }}>
        <p>
          <button className="btn btn-success btn-lg joystix" disabled={working || !draft.sentences.length} onClick={save}>
            {working ? "Saving..." : "Save and play"}
          </button>
        </p>
        <p>
          <button className="btn btn-default" disabled={working || outOfQuota} onClick={() => preview()}>
            Try again
          </button>
          {" "}
          <button className="btn btn-link" onClick={() => { setDraft(null); setError(null); }}>
            Change what I asked for
          </button>
        </p>
        {error && <p className="text-danger">{error}</p>}
        {renderQuota()}
      </div>
    </div>
  );
}
